/**
 * Hook for parsing uploaded CSV files into financial data structures.
 * Uses PapaParse for robust CSV handling.
 */

import { useState } from 'react';
import Papa from 'papaparse';
import type { MonthlySummary, Asset, Liability } from '../types/cashflow';

interface ParseResult {
  summaries: MonthlySummary[];
  assets: Asset[];
  liabilities: Liability[];
  errors: string[];
}

function parseNumber(val: unknown): number {
  const n = parseFloat(String(val ?? '0').replace(/[£,]/g, ''));
  return isNaN(n) ? 0 : n;
}

function parseSummaryRow(row: Record<string, string>): MonthlySummary | null {
  if (!row.month) return null;
  return {
    month: row.month.trim(),
    earned_income: parseNumber(row.earned_income),
    passive_income: parseNumber(row.passive_income),
    portfolio_income: parseNumber(row.portfolio_income),
    tax_expenses: parseNumber(row.tax_expenses),
    housing_expenses: parseNumber(row.housing_expenses),
    living_expenses: parseNumber(row.living_expenses),
    debt_expenses: parseNumber(row.debt_expenses),
    other_expenses: parseNumber(row.other_expenses),
  };
}

function parseAssetRow(row: Record<string, string>): Asset | null {
  if (!row.month || !row.name) return null;
  return {
    month: row.month.trim(),
    name: row.name.trim(),
    category: (row.category?.trim() as Asset['category']) ?? 'other',
    current_value: parseNumber(row.current_value),
    monthly_cashflow: parseNumber(row.monthly_cashflow),
  };
}

function parseLiabilityRow(row: Record<string, string>): Liability | null {
  if (!row.month || !row.name) return null;
  return {
    month: row.month.trim(),
    name: row.name.trim(),
    category: (row.category?.trim() as Liability['category']) ?? 'other',
    outstanding_balance: parseNumber(row.outstanding_balance),
    monthly_payment: parseNumber(row.monthly_payment),
    interest_rate: parseNumber(row.interest_rate),
  };
}

function detectFileType(filename: string, columns: string[]): 'summary' | 'assets' | 'liabilities' | 'unknown' {
  const name = filename.toLowerCase();
  if (name.includes('summary') || name.includes('monthly')) return 'summary';
  if (name.includes('asset')) return 'assets';
  if (name.includes('liabilit')) return 'liabilities';
  // fallback: detect by columns
  if (columns.includes('earned_income')) return 'summary';
  if (columns.includes('current_value')) return 'assets';
  if (columns.includes('outstanding_balance')) return 'liabilities';
  return 'unknown';
}

export function useCSVParser() {
  const [parsing, setParsing] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);

  function parseFiles(files: File[]): Promise<ParseResult> {
    setParsing(true);
    setParseErrors([]);

    const result: ParseResult = { summaries: [], assets: [], liabilities: [], errors: [] };

    const filePromises = files.map(
      (file) =>
        new Promise<void>((resolve) => {
          Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,
            complete: (parsed) => {
              const columns = parsed.meta.fields ?? [];
              const type = detectFileType(file.name, columns);

              if (type === 'summary') {
                parsed.data.forEach((row, i) => {
                  const s = parseSummaryRow(row);
                  if (s) result.summaries.push(s);
                  else result.errors.push(`${file.name} row ${i + 2}: skipped (missing month)`);
                });
              } else if (type === 'assets') {
                parsed.data.forEach((row, i) => {
                  const a = parseAssetRow(row);
                  if (a) result.assets.push(a);
                  else result.errors.push(`${file.name} row ${i + 2}: skipped (missing month/name)`);
                });
              } else if (type === 'liabilities') {
                parsed.data.forEach((row, i) => {
                  const l = parseLiabilityRow(row);
                  if (l) result.liabilities.push(l);
                  else result.errors.push(`${file.name} row ${i + 2}: skipped (missing month/name)`);
                });
              } else {
                result.errors.push(`${file.name}: could not detect file type (summary/assets/liabilities)`);
              }
              resolve();
            },
            error: (err) => {
              result.errors.push(`${file.name}: ${err.message}`);
              resolve();
            },
          });
        }),
    );

    return Promise.all(filePromises).then(() => {
      setParsing(false);
      setParseErrors(result.errors);
      return result;
    });
  }

  return { parseFiles, parsing, parseErrors };
}
